import React, { useState, useEffect } from 'react';
import printJS from 'print-js';
import moment from 'moment';
import AlertMessage from '../../AlertMessage';
import { FiPrinter, FiSearch } from 'react-icons/fi';

const ShifarasForm = () => {
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const language = localStorage.getItem('language') || 'English';
  const udiseNumber = localStorage.getItem("udiseNumber");
  const userStatus = localStorage.getItem("userStatus");
  const isEditable = userStatus === "DKglzhgYFYTDT141414HGCkkgfs22117785YFUTFybnujghhgtghghYFYVNOmC234567Omgkysfgvghyfjhyfvv56764" || localStorage.getItem("editable") === "true";

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [duplicateStudents, setDuplicateStudents] = useState([]);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);

  const [formData, setFormData] = useState({
    refNo: '',
    date: moment().format('YYYY-MM-DD'),
    udiseNo: '',
    recipient: 'To,\nThe Head,\nCast Validity Section',
    subject: 'Regarding submission of Caste validity proposal',
    salutation: 'Respected Sir/Madam,',
    bodyText: 'With reference to the above-mentioned subject, we are hereby submitting the Cast Validity Form of our college student admitted for Junior College in the academic year 2025–26.',
    academicYear: '2025–26',
    // Student Fields
    stdName: '',
    branch: '',
    standard: '',
    division: '',
    rollNo: '',
    category: '',
    grNo: '',
    serialNo: '',
    owtId: '',
    showOwtId: false
  });

  useEffect(() => {
    if (alertMessage) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
        setAlertMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  useEffect(() => {
    if (udiseNumber) {
      fetchSchoolData(udiseNumber);
    }
  }, [udiseNumber]);

  useEffect(() => {
    const fetchOutwardIds = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/outwardIds.json`);
        if (!response.ok) {
          throw new Error('Failed to fetch outward IDs');
        }
        const data = await response.json();

        if (data) {
          setFormData(prevData => ({
            ...prevData,
            owtId: data.current + 1
          }));
        }

      } catch (error) {
        console.error('Error fetching outward IDs:', error);
      }
    };
    if (udiseNumber) {
      fetchOutwardIds();
    }
  }, [udiseNumber]);

  const fetchSchoolData = async (udise) => {
    try {
      const request = indexedDB.open('SchoolManagementDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['schoolData'], 'readonly');
        const objectStore = transaction.objectStore('schoolData');
        const getReq = objectStore.get(udise);

        getReq.onsuccess = (e) => {
          const data = e.target.result;
          if (data) {
            setSchoolData(data);
            setFormData(prev => ({
              ...prev,
              udiseNo: data.udiseNumber || ''
            }));
          }
          setLoading(false);
        };
        getReq.onerror = () => setLoading(false);
      };
      request.onerror = () => setLoading(false);
    } catch (err) {
      console.error("Error reading indexedDB:", err);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'grNo') {
      if (!value.trim()) return;
      try {
        const request = indexedDB.open('SchoolManagementDB', 1);
        request.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['studentData'], 'readonly');
          const objectStore = transaction.objectStore('studentData');
          const studentRequest = objectStore.getAll();

          studentRequest.onsuccess = (ev) => {
            const students = ev.target.result;
            if (students && Array.isArray(students)) {
              const query = value.toLowerCase();
              const match = students.find(s => s.registerNo && s.registerNo.toString() === query);
              if (match) {
                autoFillStudent(match);
              }
            }
          };
        };
      } catch (error) {
        console.error("IndexedDB error:", error);
      }
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (!value.trim()) {
      clearStudentFields();
      return;
    }

    // Perform Search from indexedDB
    try {
      const request = indexedDB.open('SchoolManagementDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['studentData'], 'readonly');
        const objectStore = transaction.objectStore('studentData');
        const studentRequest = objectStore.getAll();

        studentRequest.onsuccess = (ev) => {
          const students = ev.target.result;
          if (students && Array.isArray(students)) {
            const query = value.toLowerCase();
            const matches = students.filter(s =>
              (s.registerNo && s.registerNo.toString() === query) ||
              (`${s.stdName || ''} ${s.stdFather || ''} ${s.stdSurname || ''}`.toLowerCase().includes(query))
            );

            if (matches.length >= 1) {
              const selectedStudent = matches.find(s => s.isActive !== false) || matches[0];
              autoFillStudent(selectedStudent);
            }
          }
        };
      };
    } catch (error) {
      console.error("IndexedDB error:", error);
    }
  };

  const autoFillStudent = (student) => {
    const getSchoolInitials = (name) => {
      if (!name) return 'LSF';
      const cleanName = name.replace(/[^\w\s]/gi, ''); // remove special characters
      return cleanName
        .split(/\s+/)
        .filter(word => !['of', 'and', 'the', 'a', 'an', 'in', 'to', 'for'].includes(word.toLowerCase()))
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    };

    const schoolInitials = getSchoolInitials(schoolData?.schoolName);
    const currentYear = formData.date ? moment(formData.date).format('YYYY') : moment().format('YYYY');
    const autoRefNo = `${schoolInitials}/${currentYear}/${student.registerNo || ''}`;

    const keyParts = student.id ? student.id.split('-') : [];
    const idSerialNo = keyParts.length > 0 ? keyParts[keyParts.length - 1] : '';
    const finalSerialNo = student.serialNo || idSerialNo || '';

    setFormData(prev => ({
      ...prev,
      refNo: autoRefNo,
      stdName: `${student.stdName || ''} ${student.stdFather || ''} ${student.stdSurname || ''}`.trim(),
      branch: student.branch || '',
      division: student.division || '',
      rollNo: student.rollNo || '',
      category: Array.from(new Set([student.religion, student.caste, student.subCaste, student.category].filter(Boolean))).join(' - '),
      grNo: student.registerNo || '',
      standard: student.currentClass || '',
      serialNo: finalSerialNo
    }));
    setSearchInput(student.registerNo || `${student.stdName || ''} ${student.stdSurname || ''}`.trim());
    setShowDuplicatePopup(false);
  };

  const clearStudentFields = () => {
    setFormData(prev => ({
      ...prev,
      refNo: '',
      stdName: '',
      branch: '',
      division: '',
      rollNo: '',
      category: '',
      grNo: '',
      standard: '',
      owtId: ''
    }));
  };

  const printDocument = async () => {
    const printContent = document.getElementById("cv-print-content");
    if (!printContent) return;

    // Register Outward ID in Firebase
    if (udiseNumber) {
      try {
        const outwardIdBaseUrl = `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/outwardIds`;
        const outwardIdResponse = await fetch(outwardIdBaseUrl + '.json');
        const outwardIdData = await outwardIdResponse.json() || {};

        const currentOutwardId = outwardIdData.current || 0;
        const nextOutwardId = currentOutwardId + 1;

        const outwardIdEntry = {
          serialNo: formData.serialNo || formData.grNo || '',
          reason: 'Shifaras Form Print',
          documentNumber: formData.refNo || `SHIFARAS/${nextOutwardId}`,
          date: moment(formData.date).format('DD-MM-YYYY'),
        };

        // Write new entry
        await fetch(outwardIdBaseUrl + `/${nextOutwardId}.json`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(outwardIdEntry),
        });

        // Update current
        await fetch(outwardIdBaseUrl + '/current.json', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextOutwardId),
        });

        // Update local state temporarily for next fetch
        setFormData(prev => ({
          ...prev,
          owtId: nextOutwardId + 1
        }));

      } catch (err) {
        console.error("Error logging outward ID:", err);
      }
    }

    printJS({
      printable: printContent.innerHTML,
      type: 'raw-html',
      style: `
        @media print {
          @page {
            size: A4 Portrait;
            margin: 15mm 15mm 15mm 15mm;
          }
          body {
            font-family: 'Poppins', sans-serif;
            color: #000;
            margin: 0;
            padding: 0;
          }
          .print-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .print-header-center {
            text-align: center;
            flex: 1;
          }
          .print-logo {
            width: 90px;
            height: 90px;
            object-fit: contain;
          }
          .header-title {
            font-size: 16pt;
            font-weight: bold;
            text-transform: uppercase;
            margin: 0;
          }
          .header-subtitle {
            font-size: 10pt;
            margin: 2px 0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: 11pt;
          }
          .document-body {
            margin-top: 20px;
            font-size: 12pt;
            line-height: 1.5;
          }
          .body-paragraph {
            text-indent: 40px;
            text-align: justify;
            margin-top: 15px;
            margin-bottom: 25px;
          }
          .recipient-block {
            white-space: pre-line;
            margin-bottom: 20px;
          }
          .subject-block {
            font-weight: bold;
            margin-bottom: 15px;
          }
          .student-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0 30px 0;
          }
          .student-table td {
            padding: 8px 12px;
            font-size: 12pt;
            vertical-align: top;
          }
          .student-table td:first-child {
            width: 35%;
            font-weight: bold;
          }
          .student-table td:nth-child(2) {
            width: 3%;
          }
          .closing-block {
            margin-top: 30px;
            text-align: justify;
          }
          .signature-section {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .stamp-space {
            border: 1px dashed #999;
            width: 100px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9pt;
            color: #666;
          }
          .sign-line {
            text-align: center;
            width: 220px;
          }
          .sign-text {
            font-weight: bold;
            margin-top: 10px;
          }
        }
      `
    });
  };

  return (
    <div className="main-content-of-page" style={{ background: '#f3f5f9', minHeight: 'calc(100vh - 100px)', padding: '20px' }}>
      <AlertMessage message={alertMessage} show={showAlert} />

      <div style={{
        display: 'flex',
        gap: '25px',
        flexWrap: 'wrap',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Left Side: Editable Form Panel */}
        <div style={{
          flex: '1 1 450px',
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
          padding: '25px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#1B4332', margin: 0, fontWeight: 600 }}>Shifaras Form</h3>

          </div>

          {/* Search Input */}
          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555' }}>
              Auto-Fetch Student (Enter Register No or Name)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchInputChange}
                placeholder="Search to fill fields..."
                readOnly={!isEditable && formData.grNo ? true : false}
                style={{
                  width: '100%',
                  padding: '10px 15px 10px 35px',
                  borderRadius: '8px',
                  border: '1px solid #ced4da',
                  fontSize: '15px',
                  boxSizing: 'border-box'
                }}
              />
              <FiSearch style={{ position: 'absolute', left: '12px', top: '13px', color: '#999' }} />
            </div>
          </div>

          <hr style={{ borderTop: '1px solid #e9ecef', margin: '20px 0' }} />

          {/* Reference & Date */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>O.W. No.</label>
              <input
                type="text"
                name="owtId"
                value={formData.owtId}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="Temporary O.W. No change"
              />
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', gap: '8px', width: '100%' }}>
                <input
                  type="checkbox"
                  name="showOwtId"
                  checked={formData.showOwtId === true}
                  onChange={(e) => setFormData(prev => ({ ...prev, showOwtId: e.target.checked }))}
                  style={{ margin: 0, width: '18px', height: '18px', cursor: 'pointer', flex: 'none' }}
                />
                <label style={{ fontSize: '14px', cursor: 'pointer', margin: 0, padding: 0, flex: 'none', width: 'auto', fontWeight: '500', color: '#444' }}>
                  Show on Print
                </label>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Ref No.</label>
              <input
                type="text"
                name="refNo"
                value={formData.refNo}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="e.g. LSF/2026/102"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>

          {/* UDISE No. */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>UDISE No.</label>
            <input
              type="text"
              name="udiseNo"
              value={formData.udiseNo}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          {/* Recipient & Subject */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Recipient Address</label>
            <textarea
              name="recipient"
              rows={3}
              value={formData.recipient}
              onChange={handleInputChange}
              style={{ ...inputStyle, resize: 'vertical', height: 'auto' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Subject Line</label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          {/* Body Text */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '600' }}>Body Text</label>
            <textarea
              name="bodyText"
              rows={3}
              value={formData.bodyText}
              onChange={handleInputChange}
              style={{ ...inputStyle, resize: 'vertical', height: 'auto' }}
            />
          </div>

          <div style={{ border: '1px solid #d1ecf1', background: '#e2f3f5', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
            <h5 style={{ margin: '0 0 10px 0', color: '#0c5460', borderBottom: '1px solid #bee5eb', paddingBottom: '5px' }}>Student Details</h5>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Student Name</label>
              <input type="text" name="stdName" value={formData.stdName} onChange={handleInputChange} style={inputStyleSmall} readOnly={!isEditable} />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Branch</label>
                <input type="text" name="branch" value={formData.branch} onChange={handleInputChange} style={inputStyleSmall} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Std.</label>
                <input type="text" name="standard" value={formData.standard} onChange={handleInputChange} style={inputStyleSmall} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Division</label>
                <input type="text" name="division" value={formData.division} onChange={handleInputChange} style={inputStyleSmall} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Roll No</label>
                <input type="text" name="rollNo" value={formData.rollNo} onChange={handleInputChange} style={inputStyleSmall} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>Category</label>
                <input type="text" name="category" value={formData.category} onChange={handleInputChange} style={inputStyleSmall} readOnly={!isEditable} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', fontWeight: '600' }}>GR No.</label>
                <input type="text" name="grNo" value={formData.grNo} onChange={handleInputChange} style={inputStyleSmall} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <button
              onClick={printDocument}
              style={{
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: 'pointer',
                width: '100%',
                boxShadow: '0 4px 8px rgba(40, 167, 69, 0.25)'
              }}
            >
              <FiPrinter size={20} /> Print Shifaras Form
            </button>
          </div>
        </div>

        {/* Right Side: Interactive Preview Sheet */}
        <div style={{
          flex: '2 1 600px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{ background: '#1B4332', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
            LIVE A4 PREVIEW
          </span>

          {/* Container styled like an A4 paper for user visibility */}
          <div
            id="cv-print-content"
            style={{
              background: '#fff',
              width: '210mm',
              minHeight: '297mm',
              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              padding: '20mm 20mm',
              boxSizing: 'border-box',
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12pt',
              color: '#000',
              lineHeight: '1.5'
            }}
          >
            {/* Document Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '2px solid #000',
              paddingBottom: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ width: '100px', display: 'flex', justifyContent: 'flex-start' }}>
                {schoolData?.schoolLogo && (
                  <img src={schoolData.schoolLogo} alt="Logo" style={{ width: '90px', height: '90px', objectFit: 'contain' }} />
                )}
              </div>
              <div style={{ textAlign: 'center', flex: 1, padding: '0 15px' }}>
                <h2 style={{ fontSize: '12pt', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', lineHeight: '1.2' }}>
                  {schoolData?.sansthaName || "Ladkat Educational Foundation's"}
                </h2>
                <h3 style={{ fontSize: '18pt', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', color: '#000', lineHeight: '1.2' }}>
                  {schoolData?.schoolName || "Ladkat School of Foundation"}
                </h3>
                <p style={{ fontSize: '10pt', margin: '0 0 2px 0' }}>
                  {language === 'English' ? (
                    <>
                      {schoolData?.address && (
                        <>
                          <strong>At Post : </strong>{schoolData.address},{" "}
                        </>
                      )}
                      <strong>Taluka : </strong>{schoolData?.taluka},<strong> District : </strong>{schoolData?.jilha}
                    </>
                  ) : (
                    <>
                      {schoolData?.address && (
                        <>
                          <strong>मु. पो. : </strong>{schoolData.address},{" "}
                        </>
                      )}
                      <strong> तालुका : </strong>{schoolData?.taluka}, <strong>जिल्हा : </strong>{schoolData?.jilha}
                    </>
                  )}
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '4px' }}>
                  <p style={{ fontSize: '10pt', margin: 0 }}>
                    <strong>{language === 'English' ? 'UDISE No :' : 'UDISE क्र :'}</strong> {schoolData?.udiseNumber}
                  </p>
                  {schoolData?.mobileNumber && (
                    <p style={{ fontSize: '10pt', margin: 0 }}>
                      <strong>Mob. No. :</strong> {schoolData.mobileNumber}
                    </p>
                  )}
                </div>

              </div>
              <div style={{ width: '100px' }}></div>
            </div>

            {/* Top References Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '11pt' }}>
              <div>
                {formData.showOwtId === true && (
                  <>
                    <strong>O.W. No.:</strong> {formData.owtId || 'Not Generated'}&nbsp;&nbsp;&nbsp;&nbsp;
                  </>
                )}
                <strong>Ref. No.:</strong> {formData.refNo || '________________'}
              </div>
              <div>
                <strong>Date:</strong> {formData.date ? moment(formData.date).format('DD/MM/YYYY') : '    /    /2025.'}
              </div>
            </div>

            {/* Recipient Address Block */}
            <div style={{ whiteSpace: 'pre-line', marginBottom: '25px', fontWeight: 'bold', lineHeight: '1.4' }}>
              {formData.recipient}
            </div>

            {/* Subject Row */}
            <div style={{ marginBottom: '20px', fontWeight: 'bold', textDecoration: 'underline', textAlign: 'center' }}>
              Subject: {formData.subject}
            </div>

            {/* Salutation & Main Paragraph */}
            <div style={{ marginBottom: '15px' }}>
              {formData.salutation}
            </div>

            <div style={{ textIndent: '40px', textAlign: 'justify', marginBottom: '15px' }}>
              {formData.bodyText}
            </div>

            <div style={{ fontWeight: 'bold', marginBottom: '15px', fontSize: '12pt' }}>
              The details of the student are as follows:
            </div>

            {/* Student Information Grid */}
            <table style={{ width: '90%', margin: '20px auto 30px auto', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={tdLabelStyle}>Name of the Student</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.stdName || '________________________________________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>Branch</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.branch || '________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>Std.</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.standard || '________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>Div.</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.division || '________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>Roll No.</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.rollNo || '________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>Category</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.category || '________'}</td>
                </tr>
                <tr>
                  <td style={tdLabelStyle}>GR No.</td>
                  <td style={tdColonStyle}>:</td>
                  <td style={tdValueStyle}>{formData.grNo || '________'}</td>
                </tr>
              </tbody>
            </table>

            {/* Closing Text */}
            <div style={{ textIndent: '40px', textAlign: 'justify', marginBottom: '15px' }}>
              We kindly request you to do the needful at the earliest.
            </div>

            <div style={{ marginBottom: '50px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Thanking you,</span>
              <span style={{ marginRight: '60px' }}>Yours faithfully,</span>
            </div>

            {/* Sign-off Block */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '80px' }}>
              <div style={{ border: '1px dashed #aaa', width: '110px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8pt', color: '#999', borderRadius: '4px' }}>
                College Seal Space
              </div>
              <div style={{ textAlign: 'center', width: '260px' }}>
                <div style={{ height: '40px' }}></div> {/* Spacer for actual signature */}
                <div style={{ fontWeight: 'bold', fontSize: '12pt', lineHeight: '1.3' }}>
                  Principal,<br />
                  {schoolData?.schoolName || "Ladkat School of Foundation"}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Duplicate Picker Modal Overlay */}
      {showDuplicatePopup && (
        <div className="overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '10px',
            width: '600px',
            maxWidth: '95%',
            padding: '20px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#1B4332' }}>Select Matching Student</h4>
              <button
                onClick={() => setShowDuplicatePopup(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}
              >&times;</button>
            </div>
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} className="record-tableReg">
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={thStyle}>Full Name</th>
                    <th style={thStyle}>Class</th>
                    <th style={thStyle}>Register No</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateStudents.map((st, idx) => (
                    <tr
                      key={idx}
                      onClick={() => autoFillStudent(st)}
                      style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                    >
                      <td style={tdStyle}>{`${st.stdName || ''} ${st.stdFather || ''} ${st.stdSurname || ''}`}</td>
                      <td style={tdStyle}>{st.currentClass || '-'}</td>
                      <td style={tdStyle}>{st.registerNo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #ced4da',
  fontSize: '14px',
  boxSizing: 'border-box',
  outline: 'none'
};

const inputStyleSmall = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid #ced4da',
  fontSize: '13px',
  boxSizing: 'border-box',
  outline: 'none',
  background: '#fff'
};

const tdLabelStyle = {
  padding: '6px 0',
  fontWeight: 'bold',
  width: '35%',
  verticalAlign: 'top',
  fontSize: '12pt'
};

const tdColonStyle = {
  padding: '6px 0',
  width: '3%',
  verticalAlign: 'top',
  fontSize: '12pt'
};

const tdValueStyle = {
  padding: '6px 0',
  verticalAlign: 'top',
  fontSize: '12pt'
};

const thStyle = {
  textAlign: 'left',
  padding: '10px',
  borderBottom: '2px solid #ddd',
  fontWeight: 'bold',
  fontSize: '14px'
};

const tdStyle = {
  padding: '10px',
  borderBottom: '1px solid #eee',
  fontSize: '14px'
};

export default ShifarasForm;
